import { SECTION_TIMEZONE, isoDate } from './dates.ts';
import {
  UNIFORM_ARTICLE_LABELS,
  UNIFORM_ARTICLES,
  type UniformArticle,
  type UniformCampaign,
  type UniformOrderItem,
  type UniformProduct,
} from './types.ts';

/** Tailles vêtements proposées par défaut aux parents. */
export const UNIFORM_CLOTHING_SIZES = ['6 ans', '8 ans', '10 ans', '12 ans'] as const;

/**
 * Catalogue fixe historique : prix unitaires et tailles disponibles.
 * Conservé pour les seeds / modèles par défaut et la lecture d'anciennes
 * commandes. Les campagnes récentes portent leurs propres produits en base.
 */
export const UNIFORM_CATALOG: Record<
  UniformArticle,
  { unitPriceCents: number; sizes: readonly string[] }
> = {
  pull: { unitPriceCents: 2_500, sizes: UNIFORM_CLOTHING_SIZES },
  tshirt: { unitPriceCents: 1_500, sizes: UNIFORM_CLOTHING_SIZES },
  ecusson: { unitPriceCents: 500, sizes: ['unique'] },
};

/** Brouillon de produit (création / édition campagne, avant attribution d'id). */
export interface UniformProductDraft {
  /** Id existant — absent pour une ligne nouvellement ajoutée. */
  id?: string;
  name: string;
  photoFileId: string | null;
  sizes: string[];
  unitPriceCents: number;
}

/** Modèles préremplis (pull / t-shirt / écusson) pour accélérer la création. */
export function defaultUniformProductDrafts(): UniformProductDraft[] {
  return UNIFORM_ARTICLES.map((article) => ({
    name: UNIFORM_ARTICLE_LABELS[article],
    photoFileId: null,
    sizes: [...UNIFORM_CATALOG[article].sizes],
    unitPriceCents: UNIFORM_CATALOG[article].unitPriceCents,
  }));
}

/** Libellé d'une ligne de commande (produit nommé ou article historique). */
export function uniformOrderItemLabel(item: UniformOrderItem): string {
  const named = item.name?.trim();
  if (named) return named;
  if (item.article && UNIFORM_ARTICLE_LABELS[item.article]) {
    return UNIFORM_ARTICLE_LABELS[item.article];
  }
  return 'Article';
}

/** Date civile du jour dans le fuseau de la section (`YYYY-MM-DD`). */
export function todayIsoDate(at: Date = new Date()): string {
  // `en-CA` produit déjà `YYYY-MM-DD`.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SECTION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/**
 * Une campagne accepte les commandes si elle est active et que la date limite
 * n'est pas dépassée (la date limite est inclusive).
 */
export function isUniformOrderingOpen(
  campaign: Pick<UniformCampaign, 'isActive' | 'endDate'>,
  at: Date = new Date(),
): boolean {
  if (!campaign.isActive) return false;
  return todayIsoDate(at) <= campaign.endDate;
}

/**
 * Un parent peut corriger sa commande tant que la campagne accepte encore des
 * commandes et qu'aucun article n'a été remis au local : au-delà, le staff a
 * déjà préparé ou distribué la commande et c'est à lui de la modifier.
 */
export function canParentEditUniformOrder(
  order: { pickedUpAt?: string | null; items: UniformOrderItem[] },
  campaign: Pick<UniformCampaign, 'isActive' | 'endDate'>,
  at: Date = new Date(),
): boolean {
  if (!isUniformOrderingOpen(campaign, at)) return false;
  return uniformOrderPickupStatus(order) === 'none';
}

/** Communication structurée de virement (un ou plusieurs enfants). */
export function uniformPaymentReference(
  year: number,
  children:
    | { firstName: string; lastName: string }
    | Array<{ firstName: string; lastName: string }>,
): string {
  const list = Array.isArray(children) ? children : [children];
  const names = list
    .map((child) => `${child.firstName} ${child.lastName}`.trim())
    .filter(Boolean)
    .join(' + ');
  return `Uniforme ${year} - ${names}`.replace(/\s+/g, ' ').trim();
}

/**
 * Longueurs officielles d’IBAN par code pays (ISO 13616).
 * Un numéro dont la longueur ne correspond pas est rejeté avant le contrôle
 * de clé : un chiffre décalé ne doit pas partir vers les familles.
 */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18,
  EE: 20, ES: 24, FI: 18, FR: 27, GB: 22, GI: 23, GR: 27, HR: 21, HU: 28,
  IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MT: 31,
  NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24, SM: 27,
  VA: 22,
};

/** Retire espaces et séparateurs, met en majuscules. */
export function normalizeIban(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/** Groupes de 4 caractères, forme lisible sur la confirmation et dans le mail. */
export function formatIban(raw: string): string {
  return normalizeIban(raw).replace(/(.{4})(?=.)/g, '$1 ');
}

/** Contrôle de clé IBAN (mod 97 = 1), sans passer par un grand entier. */
function ibanMod97IsValid(compact: string): boolean {
  const rearranged = compact.slice(4) + compact.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const code = char.charCodeAt(0);
    const chunk = code >= 65 && code <= 90 ? String(code - 55) : char;
    for (const digit of chunk) {
      remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
}

/**
 * Message d’erreur lisible, ou `null` si l’IBAN est exploitable pour un virement.
 * Chaîne vide = pas encore saisi.
 */
export function ibanValidationError(raw: string): string | null {
  if (typeof raw !== 'string') return 'Indiquez le numéro de compte (IBAN).';
  const trimmed = raw.trim();
  if (!trimmed) return 'Indiquez le numéro de compte (IBAN).';
  if (trimmed.length > 42) return 'Ce numéro de compte est trop long.';
  const compact = normalizeIban(trimmed);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(compact)) {
    return 'Ce numéro de compte n’est pas un IBAN valide.';
  }
  const expected = IBAN_LENGTHS[compact.slice(0, 2)];
  if (expected == null) return 'Ce pays d’IBAN n’est pas reconnu.';
  if (compact.length !== expected) {
    return `Un IBAN ${compact.slice(0, 2)} comporte ${expected} caractères.`;
  }
  if (!ibanMod97IsValid(compact)) {
    return 'Ce numéro de compte n’est pas un IBAN valide. Vérifiez chaque caractère.';
  }
  return null;
}

export function isValidIban(raw: string): boolean {
  return ibanValidationError(raw) === null;
}

/**
 * IBAN affiché aux familles : la valeur enregistrée par le Baygon, sinon le
 * repli d’environnement s’il est lui-même un IBAN valide. Jamais un placeholder.
 */
export function resolveUniformBankIban(stored: unknown, envValue?: unknown): string {
  if (typeof stored === 'string' && isValidIban(stored)) return formatIban(stored);
  if (typeof envValue === 'string' && isValidIban(envValue)) return formatIban(envValue);
  return '';
}

/**
 * Valide et normalise un panier contre le catalogue de la campagne.
 * Les prix unitaires viennent toujours du serveur (jamais du client).
 */
export function normalizeUniformOrderItems(
  raw: unknown,
  products: Array<Pick<UniformProduct, 'id' | 'name' | 'sizes' | 'unitPriceCents' | 'deletedAt'>>,
): UniformOrderItem[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error('Ajoutez au moins un article à la commande.');
  }
  if (raw.length > 30) {
    throw new Error('Trop d’articles dans la commande.');
  }

  const byId = new Map(
    products.filter((product) => !product.deletedAt).map((product) => [product.id, product]),
  );
  if (byId.size === 0) {
    throw new Error('Aucun produit n’est proposé pour cette campagne.');
  }

  const normalized: UniformOrderItem[] = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      throw new Error('Ligne de commande invalide.');
    }
    const item = entry as Record<string, unknown>;
    const productId = String(item.productId ?? '').trim();
    if (!productId) {
      throw new Error('Chaque ligne doit référencer un produit.');
    }
    const product = byId.get(productId);
    if (!product) {
      throw new Error('Produit inconnu ou retiré de la campagne.');
    }
    const size = String(item.size ?? '').trim();
    if (!product.sizes.includes(size)) {
      throw new Error(
        `Taille « ${size} » non proposée pour ${product.name.toLowerCase()}.`,
      );
    }
    const quantity = Number(item.quantity);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) {
      throw new Error('La quantité doit être un entier entre 1 et 20.');
    }
    const pickedUp = typeof item.pickedUp === 'boolean' ? item.pickedUp : undefined;
    const pickedUpQuantity =
      typeof item.pickedUpQuantity === 'number' && Number.isFinite(item.pickedUpQuantity)
        ? Math.max(0, Math.min(quantity, Math.floor(item.pickedUpQuantity)))
        : undefined;
    normalized.push({
      productId: product.id,
      name: product.name,
      size,
      quantity,
      unitPriceCents: product.unitPriceCents,
      ...(pickedUp !== undefined ? { pickedUp } : {}),
      ...(pickedUpQuantity !== undefined ? { pickedUpQuantity } : {}),
    });
  }

  return mergeUniformItems(normalized);
}

/**
 * @deprecated Lecture seule des paniers catalogue fixe (tests / anciennes données).
 * Préférer `normalizeUniformOrderItems(raw, products)`.
 */
export function normalizeLegacyUniformOrderItems(raw: unknown): UniformOrderItem[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error('Ajoutez au moins un article à la commande.');
  }
  if (raw.length > 30) {
    throw new Error('Trop d’articles dans la commande.');
  }

  const allowed = new Set<string>(UNIFORM_ARTICLES);
  const normalized: UniformOrderItem[] = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      throw new Error('Ligne de commande invalide.');
    }
    const item = entry as Record<string, unknown>;
    const article = String(item.article ?? '');
    if (!allowed.has(article)) {
      throw new Error(`Article inconnu : ${article}.`);
    }
    const typed = article as UniformArticle;
    const catalog = UNIFORM_CATALOG[typed];
    const size = String(item.size ?? '').trim();
    if (!catalog.sizes.includes(size)) {
      throw new Error(
        `Taille « ${size} » non proposée pour ${UNIFORM_ARTICLE_LABELS[typed].toLowerCase()}.`,
      );
    }
    const quantity = Number(item.quantity);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) {
      throw new Error('La quantité doit être un entier entre 1 et 20.');
    }
    const pickedUp = typeof item.pickedUp === 'boolean' ? item.pickedUp : undefined;
    const pickedUpQuantity =
      typeof item.pickedUpQuantity === 'number' && Number.isFinite(item.pickedUpQuantity)
        ? Math.max(0, Math.min(quantity, Math.floor(item.pickedUpQuantity)))
        : undefined;
    normalized.push({
      article: typed,
      name: UNIFORM_ARTICLE_LABELS[typed],
      size,
      quantity,
      unitPriceCents: catalog.unitPriceCents,
      ...(pickedUp !== undefined ? { pickedUp } : {}),
      ...(pickedUpQuantity !== undefined ? { pickedUpQuantity } : {}),
    });
  }

  return mergeUniformItems(normalized);
}

/** Fusionne les lignes identiques (même produit/article + taille). */
export function mergeUniformItems(items: UniformOrderItem[]): UniformOrderItem[] {
  const map = new Map<string, UniformOrderItem>();
  for (const item of items) {
    const key = `${item.productId ?? item.article ?? item.name ?? ''}::${item.size}`;
    const existing = map.get(key);
    if (existing) {
      existing.quantity += item.quantity;
      if (item.pickedUp !== undefined || existing.pickedUp !== undefined) {
        existing.pickedUp = Boolean(existing.pickedUp && item.pickedUp);
      }
      if (item.pickedUpQuantity !== undefined || existing.pickedUpQuantity !== undefined) {
        existing.pickedUpQuantity = (existing.pickedUpQuantity ?? 0) + (item.pickedUpQuantity ?? 0);
      }
    } else {
      map.set(key, { ...item });
    }
  }
  return [...map.values()];
}

export function uniformOrderTotalCents(items: UniformOrderItem[]): number {
  return items.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0);
}

/** Indique si une ligne de commande a été retirée au local. */
export function isUniformItemPickedUp(
  item: UniformOrderItem,
  orderPickedUpAt?: string | null,
): boolean {
  if (item.pickedUp !== undefined) return item.pickedUp;
  if (item.pickedUpQuantity !== undefined) return item.pickedUpQuantity >= item.quantity;
  return Boolean(orderPickedUpAt);
}

/** Nombre d'exemplaires retirés pour une ligne de commande. */
export function uniformItemPickedUpCount(
  item: UniformOrderItem,
  orderPickedUpAt?: string | null,
): number {
  if (item.pickedUpQuantity !== undefined) {
    return Math.max(0, Math.min(item.quantity, item.pickedUpQuantity));
  }
  if (item.pickedUp !== undefined) {
    return item.pickedUp ? item.quantity : 0;
  }
  return orderPickedUpAt ? item.quantity : 0;
}

/**
 * Statut global de retrait d'une commande :
 * - 'none' : aucun article retiré
 * - 'partial' : une partie des articles retirés
 * - 'complete' : tous les articles retirés
 */
export function uniformOrderPickupStatus(order: {
  pickedUpAt?: string | null;
  items: UniformOrderItem[];
}): 'none' | 'partial' | 'complete' {
  if (!order.items || order.items.length === 0) {
    return order.pickedUpAt ? 'complete' : 'none';
  }
  let totalQty = 0;
  let pickedQty = 0;
  for (const item of order.items) {
    totalQty += item.quantity;
    pickedQty += uniformItemPickedUpCount(item, order.pickedUpAt);
  }
  if (pickedQty === 0) return 'none';
  if (pickedQty >= totalQty) return 'complete';
  return 'partial';
}

/** Calcule le nombre total d'articles retirés et le total attendu. */
export function uniformOrderPickedUpCount(order: {
  pickedUpAt?: string | null;
  items: UniformOrderItem[];
}): { picked: number; total: number } {
  let total = 0;
  let picked = 0;
  for (const item of order.items ?? []) {
    total += item.quantity;
    picked += uniformItemPickedUpCount(item, order.pickedUpAt);
  }
  return { picked, total };
}

/**
 * Valide un brouillon de produit côté API / formulaire.
 * Renvoie le produit normalisé ou lève une Error lisible.
 */
export function normalizeUniformProductDraft(raw: unknown, index: number): UniformProductDraft {
  if (!raw || typeof raw !== 'object') {
    throw new Error(`Produit #${index + 1} invalide.`);
  }
  const entry = raw as Record<string, unknown>;
  const name = String(entry.name ?? '').trim();
  if (!name || name.length > 120) {
    throw new Error(`Le nom du produit #${index + 1} est obligatoire (max 120 caractères).`);
  }
  const unitPriceCents = Number(entry.unitPriceCents);
  if (!Number.isInteger(unitPriceCents) || unitPriceCents < 0 || unitPriceCents > 1_000_000_000) {
    throw new Error(`Le prix du produit « ${name} » doit être un entier de centimes ≥ 0.`);
  }
  const sizesRaw = entry.sizes;
  if (!Array.isArray(sizesRaw) || sizesRaw.length === 0) {
    throw new Error(`Indiquez au moins une taille pour « ${name} ».`);
  }
  if (sizesRaw.length > 30) {
    throw new Error(`Trop de tailles pour « ${name} ».`);
  }
  const sizes: string[] = [];
  const seen = new Set<string>();
  for (const sizeRaw of sizesRaw) {
    const size = String(sizeRaw ?? '').trim();
    if (!size || size.length > 40) {
      throw new Error(`Taille invalide pour « ${name} ».`);
    }
    const key = size.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    sizes.push(size);
  }
  if (sizes.length === 0) {
    throw new Error(`Indiquez au moins une taille pour « ${name} ».`);
  }
  const photoFileId =
    entry.photoFileId == null || entry.photoFileId === ''
      ? null
      : String(entry.photoFileId).trim();
  if (photoFileId && photoFileId.length > 80) {
    throw new Error(`Photo invalide pour « ${name} ».`);
  }
  const id = entry.id == null || entry.id === '' ? undefined : String(entry.id).trim();
  return {
    id,
    name,
    photoFileId,
    sizes,
    unitPriceCents,
  };
}

/** Exposé pour les tests / affichages qui ont besoin d’une date ISO civile. */
export { isoDate };
